/**
 * Shared date/time formatting utilities.
 *
 * Consolidates duplicate formatters from SharedLinkViewer, ShareViewer,
 * SharedConversations, FinancialsTab, and scheduledTasksApi.
 */

/** Parse Firestore timestamps ({ _seconds }), Date objects, or ISO strings into a Date. */
export function parseTimestamp(timestamp: any): Date | null {
  if (!timestamp) return null;
  try {
    let date: Date;
    if (timestamp._seconds) {
      date = new Date(timestamp._seconds * 1000);
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else {
      date = new Date(timestamp);
    }
    return isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
}

/** Absolute locale timestamp: "Jan 15, 2026, 2:30 PM". */
export function formatTimestamp(timestamp: any): string {
  const date = parseTimestamp(timestamp);
  if (!date) return '';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Abbreviated relative time for past dates.
 * "just now" → "3m ago" → "2h ago" → "5d ago" → falls back to locale date after 7 days.
 * Accepts Firestore timestamps, ISO strings, or Date objects.
 */
export function formatTimeAgo(timestamp: string | Date | any | null): string {
  if (!timestamp) return '';
  const date = parseTimestamp(timestamp);
  if (!date) return '';

  const diff = Date.now() - date.getTime();
  if (diff < 0) return 'just now';
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
}

/**
 * Full-text bidirectional relative time.
 * Past: "3 minutes ago", "2 hours ago", "5 days ago"
 * Future: "in 3 minutes", "in 2 hours", "in 5 days"
 */
export function formatRelativeTime(dateStr: string | undefined): string {
  if (!dateStr) return 'Never';

  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffMins = Math.round(diffMs / 60000);
  const diffHours = Math.round(diffMs / 3600000);
  const diffDays = Math.round(diffMs / 86400000);

  if (diffMs < 0) {
    if (diffMins > -60) return `${Math.abs(diffMins)} minutes ago`;
    if (diffHours > -24) return `${Math.abs(diffHours)} hours ago`;
    return `${Math.abs(diffDays)} days ago`;
  } else {
    if (diffMins < 60) return `in ${diffMins} minutes`;
    if (diffHours < 24) return `in ${diffHours} hours`;
    return `in ${diffDays} days`;
  }
}
